// ============================================
// Customers Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { CustomerListParams, CreateCustomerRequest, UpdateCustomerRequest, CustomerStats } from '@conai/shared-types';
import { useCustomersService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

export function useCustomers(params: CustomerListParams = {}) {
  const customersService = useCustomersService();

  return useQuery({
    queryKey: queryKeys.customers.list(params as Record<string, unknown>),
    queryFn: () => customersService.list(params),
  });
}

export function useCustomersStats() {
  const customersService = useCustomersService();

  return useQuery<CustomerStats>({
    queryKey: [...queryKeys.customers.all, 'stats'] as const,
    queryFn: () => customersService.getStats(),
  });
}

export function useCreateCustomer() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreateCustomerRequest) => customersService.create(data),
    onSuccess: (newCustomer) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerCreated', { name: newCustomer.full_name }));
    },
  });
}

export function useUpdateCustomer() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    /**
     * Bug #104: PATCH chứ không PUT.
     *
     * `customersService.update()` gọi PUT — DRF hiểu PUT là "thay TOÀN BỘ bản
     * ghi", nên mọi field không có trong payload đều bị coi là muốn xoá/thiếu.
     * Màn Customers chỉ có 4 ô nhập, nên bật một cái checkbox marketing consent
     * lại kéo theo việc khai lại cả hồ sơ khách — khách nào thiếu dữ liệu
     * (chưa có SĐT/email, chưa có cấp bậc) là 400 ngay.
     *
     * PATCH merge theo key: gửi key nào sửa key đó, còn lại giữ nguyên.
     */
    mutationFn: ({ id, data }: { id: string; data: Partial<UpdateCustomerRequest> }) =>
      customersService.patch(id, data),
    onSuccess: (updatedCustomer) => {
      queryClient.setQueryData(queryKeys.customers.detail(updatedCustomer.id), updatedCustomer);
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerUpdated', { name: updatedCustomer.full_name }));
    },
  });
}

// ĐÃ BỎ (23/09/2026): `useLinkCustomerUser`, `useUnlinkCustomerUser`.
//
// Hệ thống tự nhận hồ sơ khách khi tài khoản xác minh xong (BE 23/09/2026), nên
// không còn thao tác nối tay trong quy trình. Endpoint `link-user`/`unlink-user`
// phía BE VẪN CÒN — ba ca cần người quyết (tài khoản chưa xác minh, hai hồ sơ
// trùng một SĐT, SĐT trùng hồ sơ của tài khoản khác) do BE ops xử lý qua Django
// admin, có task quét hằng ngày gửi email digest tới `CONAI_OPS_ALERT_EMAILS`.
//
// Khách báo "app không thấy điểm" -> ticket về ops, chạy `diagnose_customer
// --phone <số>`. Đừng dựng lại hook ở đây để "tiện sửa nhanh".

export function useDeleteCustomer() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => customersService.delete(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: queryKeys.customers.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerDeleted'));
    },
  });
}
